import React, { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import Avatar from "../../assets/3d_avatar_26.png";
import EmptyProjectsState from "../../components/UI_components/EmptyProject";
import { FiFilter, FiChevronDown, FiSearch, FiArchive } from "react-icons/fi";
import { TbArrowsSort } from "react-icons/tb";
import { BiUndo } from "react-icons/bi";
import { ProjectApi } from "api/projects/service";

// Skeleton component for loading state
const SkeletonCard = () => (
  <div className="bg-white w-full rounded-[12px] border border-[#78797A] shadow p-4 animate-pulse">
    <div className="flex justify-between items-center">
      <div className="space-y-2">
        <div className="h-4 bg-gray-300 rounded w-3/4"></div>
        <div className="h-3 bg-gray-300 rounded w-1/2"></div>
      </div>
      <div className="flex space-x-2">
        <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
        <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
        <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
      </div>
      <div className="h-4 bg-gray-300 rounded w-1/4"></div>
    </div>
  </div>
);

// API functions
const fetchSites = async (organizationId) => {
  const data = await ProjectApi.listSitesByOrganization(organizationId);
  return data;
};

const fetchSiteMembers = async (siteId) => {
  const data = await ProjectApi.listSiteMembers(siteId);
  return data.status ? data.data : [];
};

const fetchSitesWithMembers = async (organizationId) => {
  const userData = JSON.parse(localStorage.getItem("user"));
  const sites = await fetchSites(organizationId);

  if (!sites || sites.length === 0) {
    return [];
  }

  // Fetch members for all sites in parallel
  const sitesWithMembers = await Promise.all(
    sites.map(async (site) => {
      try {
        const members = await fetchSiteMembers(site.id);

        // Check if the current user is part of the members of the site
        const isUserMember =
          members.some((member) => member.user_id === userData.user) ||
          userData?.profession === "owner";

        return isUserMember ? { ...site, name: site.site_name, members } : null;
      } catch (error) {
        console.error(`Error fetching members for site ${site.id}:`, error);
        return null;
      }
    })
  );

  return sitesWithMembers.filter((site) => site !== null);
};

const updateSiteArchiveStatus = async ({ siteId, activeFlag }) => {
  return await ProjectApi.updateSiteArchiveStatus({ siteId, activeFlag });
};

const MyProjects = ({ filteredSites, onNewProjectClick, onBack }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("default");
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [activeTab, setActiveTab] = useState("active");
  const [organization_id, setOrganizationId] = useState(null);
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const statusFilterButtonRef = useRef(null);
  const statusFilterDropdownRef = useRef(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // const organization_id = JSON.parse(localStorage.getItem("organization"))?.id;
  useEffect(() => {
    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    setOrganizationId(orgId);
  }, [localStorage.getItem("organization")]);

  // Fetch sites with React Query
  // const {
  //   data: sites = [],
  //   isLoading,
  //   error,
  //   isError,
  // } = useQuery({
  //   queryKey: ["sites", organization_id],
  //   queryFn: () => fetchSitesWithMembers(organization_id),
  //   enabled: !!organization_id,
  //   staleTime: 5 * 60 * 1000, // 5 minutes
  //   cacheTime: 1 * 60 * 1000, // 1 minutes
  //   refetchOnWindowFocus: true,
  //   retry: 2,
  // });

  useEffect(() => {
    const fetchData = async () => {
      if (!organization_id) return;

      setIsLoading(true);
      try {
        const sitesData = await fetchSitesWithMembers(organization_id);
        setSites(sitesData);
      } catch (error) {
        console.error("Error fetching sites:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [organization_id]);

  const deleteSite = async (siteId) => {
    try {
      await ProjectApi.deleteSite(siteId);
      // Invalidate and refetch sites after deletion
      queryClient.invalidateQueries(["sites", organization_id]);
    } catch (error) {
      console.error("Error deleting site:", error);
    }
  };

  // Archive/Unarchive mutation
  const archiveMutation = useMutation({
    mutationFn: updateSiteArchiveStatus,
    onSuccess: (data, variables) => {
      // Optimistically update the cache
      queryClient.setQueryData(["sites", organization_id], (oldData) => {
        if (!oldData) return oldData;

        return oldData.map((site) =>
          site.id === variables.siteId
            ? { ...site, active_flag: variables.activeFlag }
            : site
        );
      });
    },
    onError: (error) => {
      console.error("Error updating site archive status:", error);
      // Optionally show a toast notification here
    },
  });

  // Get all unique construction status values
  const uniqueStatuses = useMemo(() => {
    return ["All", ...new Set(sites.map((site) => site.constructionStatus))];
  }, [sites]);

  // Function to toggle site archive status
  const toggleArchiveStatus = (site) => {
    archiveMutation.mutate({
      siteId: site.id,
      activeFlag: !site.active_flag,
    });
  };

  // Function to sort sites based on sortOrder
  const sortSites = (sitesToSort) => {
    switch (sortOrder) {
      case "name-asc":
        return [...sitesToSort].sort((a, b) =>
          a.site_name.localeCompare(b.site_name)
        );
      case "name-desc":
        return [...sitesToSort].sort((a, b) =>
          b.site_name.localeCompare(a.site_name)
        );
      case "status-asc":
        return [...sitesToSort].sort((a, b) =>
          a.constructionStatus.localeCompare(b.constructionStatus)
        );
      case "status-desc":
        return [...sitesToSort].sort((a, b) =>
          b.constructionStatus.localeCompare(a.constructionStatus)
        );
      default:
        return sitesToSort;
    }
  };

  // Function to filter sites based on status, search query, and active flag
  const filterSites = useMemo(() => {
    let filteredResults = [...sites];

    // Filter by active_flag based on selected tab
    filteredResults = filteredResults.filter((site) =>
      activeTab === "active"
        ? site.active_flag !== false
        : site.active_flag === false
    );

    // Filter by construction status
    if (statusFilter !== "All") {
      filteredResults = filteredResults.filter(
        (site) => site.constructionStatus === statusFilter
      );
    }

    // Filter by search query
    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      filteredResults = filteredResults.filter(
        (site) =>
          site.site_name.toLowerCase().includes(query) ||
          site.address.toLowerCase().includes(query) ||
          site.city.toLowerCase().includes(query) ||
          site.constructionStatus.toLowerCase().includes(query)
      );
    }

    // Sort the filtered results
    return sortSites(filteredResults);
  }, [sites, activeTab, statusFilter, searchQuery, sortOrder]);

  // Get displayed sites after filtering and sorting
  const displayedSites = filterSites;

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery("");
    setStatusFilter("All");
    setSortOrder("default");
  };

  // Close status filter dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showStatusFilter) return;
      const clickedInsideDropdown = statusFilterDropdownRef.current?.contains(
        event.target
      );
      const clickedOnButton = statusFilterButtonRef.current?.contains(
        event.target
      );
      if (!clickedInsideDropdown && !clickedOnButton) {
        setShowStatusFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showStatusFilter]);

  // Handle error state
  // if (isError) {
  //   return (
  //     <div className="p-2 bg-gray-50 min-h-screen">
  //       <h1 className="text-2xl font-semibold text-[#343C6A] mb-6">
  //         My Projects
  //       </h1>
  //       <div className="bg-red-50 border border-red-200 rounded-lg p-4">
  //         <p className="text-red-700">
  //           Error loading projects. Please try again.
  //         </p>
  //         <button
  //           onClick={() =>
  //             queryClient.refetchQueries(["sites", organization_id])
  //           }
  //           className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
  //         >
  //           Retry
  //         </button>
  //       </div>
  //     </div>
  //   );
  // }

  // Render empty state if no sites and not loading
  if (!isLoading && sites.length === 0) {
    return (
      <div className="p-2 bg-gray-50 min-h-screen">
        <h1 className="text-2xl font-semibold text-[#343C6A] mt-3 mb-4">
          My Projects
        </h1>
        <EmptyProjectsState onNewProjectClick={onNewProjectClick} />
      </div>
    );
  }

  return (
    <div className="p-2 min-h-screen">
      <h1 className="text-2xl font-semibold text-[#343C6A] mt-3 mb-4">
        My Projects
      </h1>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`py-2 px-4 font-medium text-sm ${
            activeTab === "active"
              ? "text-purple-600 border-b-2 border-purple-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => handleTabChange("active")}
        >
          Active Projects
        </button>
        <button
          className={`py-2 px-4 font-medium text-sm ${
            activeTab === "archived"
              ? "text-purple-600 border-b-2 border-purple-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => handleTabChange("archived")}
        >
          Archived Projects
        </button>
      </div>

      {/* Search and filter controls */}
      <div className="flex flex-wrap gap-2 mb-4">
        {/* Search input */}
        <div className="relative flex-grow max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            className="pl-10 pr-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
            placeholder={`Search ${
              activeTab === "active" ? "active" : "archived"
            } projects...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Status filter dropdown */}
        <div className="relative">
          <button
            className="flex items-center space-x-1 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-600"
            onClick={() => setShowStatusFilter(!showStatusFilter)}
            ref={statusFilterButtonRef}
          >
            <FiFilter className="text-gray-600" />
            <span className="text-gray-700">Status: {statusFilter}</span>
            <FiChevronDown className="text-gray-600" />
          </button>

          {showStatusFilter && (
            <div
              className="absolute z-10 mt-1 w-56 bg-white border border-gray-300 rounded-lg shadow-lg"
              ref={statusFilterDropdownRef}
            >
              {uniqueStatuses.map((status) => (
                <div
                  key={status}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    setStatusFilter(status);
                    setShowStatusFilter(false);
                  }}
                >
                  {status}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sort dropdown */}
        <div className="relative">
          <select
            className="flex items-center space-x-1 px-6 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-600 appearance-none pl-8"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="default">Sort by</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
            <option value="status-asc">Status (A-Z)</option>
            <option value="status-desc">Status (Z-A)</option>
          </select>
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <TbArrowsSort className="text-gray-600" />
          </div>
        </div>
      </div>

      {/* Project results count */}
      <div className="mb-4">
        <p className="text-gray-600">
          {displayedSites.length}{" "}
          {activeTab === "active" ? "active" : "archived"} project
          {displayedSites.length !== 1 ? "s" : ""} found
          {statusFilter !== "All" ? ` with status "${statusFilter}"` : ""}
        </p>
      </div>

      <div className="space-y-4 w-full mx-auto">
        {isLoading
          ? // Show skeletons only on initial load
            Array.from({ length: 5 }).map((_, index) => (
              <SkeletonCard key={index} />
            ))
          : displayedSites.map((site, index) => (
              <div
                key={site.id || index}
                onClick={() => {
                  localStorage.setItem("site", site.id);
                  localStorage.setItem("siteName", site.site_name);
                  localStorage.setItem("siteData", JSON.stringify(site));
                  navigate(`/site/${site.id}/home`);
                }}
                className="bg-white w-full rounded-[12px] border border-[#78797A] shadow hover:shadow-lg hover:bg-[#ebeaec] flex flex-col gap-1 cursor-pointer py-3 px-3 pr-0 relative"
                style={{ transition: "transform 0.2s" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.01)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <div className="flex flex-row justify-between items-center">
                  <div className="space-y-1" style={{ width: "25%" }}>
                    <h3 className="text-base font-semibold text-gray-900">
                      {site.site_name}
                    </h3>
                    <p className="text-gray-500 text-xs">
                      {site.address} {site.pincode}
                    </p>
                  </div>

                  {/* Avatars (second column) */}
                  <div
                    className="flex flex-row items-center justify-end"
                    style={{ width: "25%", transform: "translateX(-64px)" }}
                  >
                    {site?.members?.slice(0, 3).map((member, i) => (
                      <div
                        key={i}
                        style={{
                          transform: `translateX(-${i * 8}px)`,
                        }}
                        className="w-8 h-8 rounded-full border-2 border-white ring-1 ring-white"
                      >
                        <img
                          src={member.userpic || Avatar}
                          alt="Team member"
                          className="w-full h-full rounded-full object-cover"
                        />
                      </div>
                    ))}
                    {site?.members?.length > 3 && (
                      <div
                        style={{
                          marginLeft: "-16px",
                        }}
                        className="w-8 h-8 rounded-full border-2 border-white bg-[#E0E5F2] flex items-center justify-center text-[10px] text-[#4318FF] ring-1 ring-white"
                      >
                        +{site?.members?.length - 3}
                      </div>
                    )}
                  </div>

                  {/* Estimated Budget (third column) */}
                  <div
                    className="text-left px-2"
                    style={{ width: "25%", transform: "translateX(26px)" }}
                  >
                    <div className="text-gray-500 text-[11px]">
                      Estimated Budget
                    </div>
                    <div className="text-gray-900 text-sm font-medium">
                      {site?.esrimated_cost
                        ? `₹ ${Number(site.esrimated_cost).toLocaleString()}`
                        : "N/A"}
                    </div>
                  </div>

                  <div
                    className="flex items-center justify-end gap-2 px-2"
                    style={{ width: "25%" }}
                  >
                    <span
                      className={`px-2 py-0 rounded-full text-xs font-medium ${
                        site.constructionStatus === "Ongoing"
                          ? "bg-blue-100 text-blue-800"
                          : site.constructionStatus === "Completed"
                          ? "bg-green-100 text-green-800"
                          : site.constructionStatus === "Paused"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {site.constructionStatus}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!archiveMutation.isLoading) {
                          toggleArchiveStatus(site);
                        }
                      }}
                      className={`flex items-center gap-1 py-[2px] px-2 rounded-md text-xs font-medium 
                        ${
                          activeTab === "active"
                            ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                        } 
                        transition-colors ${
                          archiveMutation.isLoading
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                      disabled={archiveMutation.isLoading}
                      title={
                        activeTab === "active"
                          ? "Archive this site"
                          : "Unarchive this site"
                      }
                    >
                      {activeTab === "active" ? (
                        <>
                          <FiArchive className="text-gray-600" />
                          <span>Archive</span>
                        </>
                      ) : (
                        <>
                          <BiUndo className="text-gray-600" />
                          <span>Unarchive</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSite(site.id);
                      }}
                      className="flex items-center gap-1 py-[2px] px-2 rounded-md text-xs font-medium 
                        bg-red-100 text-red-700 hover:bg-red-200"
                      title="Delete this site"
                    >
                      <span>Delete</span>
                    </button>
                  </div> */}
                </div>
              </div>
            ))}

        {/* No results message */}
        {!isLoading && displayedSites.length === 0 && sites.length > 0 && (
          <div className="text-center p-8">
            <div className="text-gray-500 mb-2">
              {activeTab === "active"
                ? "No active projects match your search criteria"
                : "No archived projects found"}
            </div>
            {searchQuery || statusFilter !== "All" ? (
              <button
                className="text-purple-600 hover:text-purple-800 underline"
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("All");
                  setSortOrder("default");
                }}
              >
                Clear filters
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProjects;
